import React, { useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { WqToast as toast } from "../../utils/themedToast";
import { WqSwal as Swal } from "../../utils/themedSwal";
import { getEmbedContext, notifyParent } from "../../utils/embed.js";
import config from "../../config.js";
import { hideLoader, showLoader } from "../../utils/loader.jsx";
import { finalizePaymentSuccess, showPaymentError } from "../../utils/paymentOutcomes.js";
import { UssdSelector } from "./UssdSelector.jsx";

export const UssdTestMode = ({ banks }) => {
  const [isloading, setIsloading] = useState(false);
  const [tab, setTab] = useState(1);
  const [bankCode, setBankCode] = useState("");
  const [selectedBank, setSelectedBank] = useState(null);
  const { isEmbedded } = getEmbedContext();
  const refNo = sessionStorage.getItem("tranId") || new URLSearchParams(window.location.search).get("_tranId");

  // Code generation still hits the real endpoint — sandbox returns a dummy
  // dial string safely, same as a live account. Only confirmation below is
  // simulated, since no real transfer exists for the status endpoint to see.
  const handleSelectBank = (e) => {
    setSelectedBank(e?.label);
    const productDetails = JSON.parse(sessionStorage.getItem("product_data") || "null");
    const customer = productDetails?.customer;
    const transactionRef = Date.now() + Math.floor(Math.random() * 9);
    const payload = {
      amount: productDetails?.amount,
      bankId: e?.value,
      channel: "USSD",
      customerEmail: customer?.email,
      customerId: customer?.customerId,
      merchantId: customer?.merchantId,
      paymentDescription: productDetails?.description,
      subMerchantName: productDetails?.merchant_business_name,
      transactionReference: transactionRef,
      transactionType: 0,
      transactionRefNo: refNo,
    };
    setIsloading(true);
    showLoader();
    axios
      .post(`${config.API_ROOT}/ussd-service/api/v2/transaction`, payload)
      .then((response) => {
        setBankCode(response?.data?.data?.responseDetails?.ussdString);
        setTab(2);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.details?.[0] || err?.response?.data?.message);
        showPaymentError(err, "Could not activate with bank ussd code");
      })
      .finally(() => {
        setIsloading(false);
        hideLoader();
      });
  };

  const copyCode = () => {
    navigator.clipboard.writeText(bankCode);
    toast.success(`${selectedBank} Code copied`);
  };

  // No status endpoint call — same contract as BankTransferTestMode: pick an
  // outcome, finalize through it directly, and tell the dashboard (if
  // embedded) that this was simulated so its poll doesn't spin forever.
  const simulatePayment = () => {
    Swal.fire({
      title: "Simulate transfer outcome",
      text: "This is a test transaction — no real funds move. Pick what you want to simulate.",
      icon: "question",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Simulate success",
      denyButtonText: "Simulate failure",
      cancelButtonText: "Back",
      confirmButtonColor: "#2e7d32",
      denyButtonColor: "#c0392b",
    }).then((result) => {
      if (result.isConfirmed) {
        finalizePaymentSuccess({
          message: "Test payment simulated successfully",
          refNo,
          isEmbedded,
          notifyExtra: { simulated: true },
        });
      } else if (result.isDenied) {
        showPaymentError(null, "Simulated transfer failure. Please try again.");
        if (isEmbedded) {
          notifyParent("failed", { simulated: true });
        }
      }
    });
  };

  return (
    <UssdSelector
      tab={tab}
      banks={banks}
      onSelectBank={handleSelectBank}
      isLoading={isloading}
      bankCode={bankCode}
      selectedBank={selectedBank}
      onCopyCode={copyCode}
      onConfirm={simulatePayment}
      confirmText="I have completed the payment"
      banner={
        <TestBanner>
          <span>TEST MODE</span> — this transaction is simulated, no funds will move.
        </TestBanner>
      }
    />
  );
};

const TestBanner = styled.div`
  margin: 0 10px 10px;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: #fff4e5;
  color: #9a5b00;
  font-size: 12px;
  text-align: center;
  span {
    font-weight: 700;
  }
`;